/**
 * Discussion.
 *
 * DESIGN
 *   Two levels only — a note and replies to it. Deeper nesting reads badly in a
 *   side panel and nobody has ever wanted it on a drawing review.
 *
 *   The shape is a chat box, not a feed: oldest at the top, newest at the
 *   bottom, and the composer docked below the scrolling list. That is what
 *   makes the newest thing and the place you answer it adjacent, and it is why
 *   the page itself never scrolls — only the conversation does.
 *
 *   Mentions and file references are parsed by @lattice/core (the same
 *   functions the server uses to decide who to notify), so what the composer
 *   highlights and who actually gets told can never diverge.
 *
 *   A comment is drawn from the body EVERY time. Nothing is precomputed and
 *   stored, so fixing the renderer fixes every comment ever written, including
 *   the ones already in Postgres.
 */
import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type KeyboardEvent, type ReactNode,
} from 'react'
import {
  MessageSquarePlus, Send, MoreHorizontal, Reply, ArrowDown, AlertTriangle, Type,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { create } from 'zustand'
import {
  buildThreads, parseMentions, validateBody, triggerAt, applyTrigger, formatFileRef,
  fileRefsIn, basename, dirname, SearchIndex, MAX_COMMENT_LENGTH,
  type Comment, type Path, type ProjectId, type Span, type Thread, type UserId, ROSTER,
} from '@lattice/core'
import { useWorkspace, personOf, commentsByPath, filesByProject } from '@/store/workspace'
import { api } from '@/store/api'
import { CommentBody, FileChip } from './CommentBody'
import { FileIcon } from './FileIcon'
import { Avatar, Button, Tooltip, EmptyState, Spinner, Kbd, MOD, Highlight } from './ui/primitives'
import { formatRelative, formatExact, ellipsisPath } from '@/lib/format'
import { cn } from '@/lib/utils'

/* ------------------------------------------------------------------ */
/* Optimistic comments                                                 */
/* ------------------------------------------------------------------ */

/**
 * DATA DEFINITION
 *   A Pending is { id, projectId, path?, parentId?, body, authorId, error? }.
 *   Interpretation: a comment THIS window has submitted and not yet seen the
 *   server acknowledge; `error` is set when the write came back refused.
 *   `id` is local and means nothing anywhere else.
 *
 * WHY IT IS NOT IN THE REPLICA
 *   The replica is server truth and is written to IndexedDB. A row that may
 *   never have existed on the server must not be able to get in there and
 *   outlive the tab. Keeping pending writes beside the replica instead means
 *   the worst case of a failed post is a visible "could not send" with the
 *   person's words still in front of them — which is the one thing a comment
 *   box must never lose.
 */
interface Pending {
  readonly id: string
  readonly projectId: ProjectId
  readonly path?: string
  readonly parentId?: string
  readonly body: string
  readonly authorId: UserId
  readonly error?: string
}

interface PendingState {
  rows: Pending[]
  begin: (row: Omit<Pending, 'id'>) => Pending
  setError: (id: string, error?: string) => void
  drop: (id: string) => void
}

/**
 * A pending comment's id is the one the SERVER will store it under.
 *
 * It must therefore be a real UUID, minted before the first attempt and reused
 * on every retry: the server is idempotent on it, so a retry after a request
 * it actually committed — but failed to acknowledge, because the connection
 * dropped between the write and the response — returns the existing row rather
 * than writing the comment twice.
 * newCommentId : -> String
 */
function newCommentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  // Older embedded webviews lack randomUUID. Shape matters: the server checks it.
  const hex = (n: number): string => Array.from({ length: n }, () =>
    Math.floor(Math.random() * 16).toString(16)).join('')
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`
}

const usePending = create<PendingState>()((set) => ({
  rows: [],
  begin: row => {
    const full: Pending = { ...row, id: newCommentId() }
    set(state => ({ rows: [...state.rows, full] }))
    return full
  },
  setError: (id, error) => set(state => ({
    rows: state.rows.map(r => (r.id === id ? { ...r, error } : r)),
  })),
  drop: id => set(state => ({ rows: state.rows.filter(r => r.id !== id) })),
}))

/**
 * Send a pending comment and reconcile the replica with what came back.
 * deliver : Pending -> Promise<void>
 */
async function deliver(row: Pending): Promise<void> {
  usePending.getState().setError(row.id, undefined) // a retry starts clean
  try {
    const { comment } = await api.addComment({
      // Reusing the row's id is what makes a retry safe; see newCommentId.
      id: row.id,
      projectId: row.projectId, path: row.path, parentId: row.parentId, body: row.body,
    })
    absorb(comment)
    usePending.getState().drop(row.id)
  } catch (e) {
    usePending.getState().setError(row.id, (e as Error).message || 'could not send')
  }
}

/**
 * Put a comment the server just returned into the replica immediately.
 *
 * The cursor is deliberately left alone. A cursor means "I have seen
 * EVERYTHING up to here", and one row we happen to hold is not everything —
 * advancing it to this row's seq would skip whatever a colleague wrote in
 * between. The long poll will deliver this same row again; merging is by id
 * and idempotent, so that costs nothing.
 * absorb : Comment -> void
 */
function absorb(comment: Comment): void {
  useWorkspace.getState().applyDelta({ changes: [], comments: [comment], cursor: 0 })
}

/* ------------------------------------------------------------------ */
/* Staying at the bottom                                               */
/* ------------------------------------------------------------------ */

/** Within this many pixels of the end counts as "reading the newest". */
const NEAR_BOTTOM_PX = 64

/**
 * Keep a scrolling list pinned to its newest row — unless the reader has
 * scrolled up, in which case nothing may move under them.
 *
 * `signal` is a value that changes exactly when the content does; the list is
 * re-pinned in a LAYOUT effect so the jump happens in the same frame the row
 * appears and is never seen.
 *
 * useStickToBottom : String -> {ref, atBottom, onScroll, jump}
 */
function useStickToBottom(signal: string) {
  const ref = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(true)
  // A ref as well as state: the layout effect below must read the CURRENT
  // value, not the one captured when the component last rendered.
  const pinned = useRef(true)

  const onScroll = useCallback(() => {
    const el = ref.current
    if (!el) return
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX
    pinned.current = bottom
    setAtBottom(bottom)
  }, [])

  useLayoutEffect(() => {
    const el = ref.current
    if (el && pinned.current) el.scrollTop = el.scrollHeight
  }, [signal])

  /**
   * Go back to the newest row, and follow along from then on.
   *
   * Deliberately an instant jump rather than a smooth one: smooth scrolling is
   * driven by animation frames, which a background tab does not get, and a
   * "jump" that silently does nothing is worse than one that is abrupt. A row
   * that renders a frame later is caught by the layout effect above, which is
   * now pinned.
   */
  const jump = useCallback(() => {
    pinned.current = true
    setAtBottom(true)
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  return { ref, atBottom, onScroll, jump }
}

/* ------------------------------------------------------------------ */
/* The conversation                                                    */
/* ------------------------------------------------------------------ */

/** One shared empty list, so "no comments here" keeps its identity across renders. */
const NONE: readonly Comment[] = []

/** Threads about one file, or about the project when `path` is undefined. */
export function CommentPanel({ projectId, path }: { projectId: ProjectId; path?: string }) {
  const comments = useWorkspace(s => s.comments)
  const byPath = useMemo(() => commentsByPath(comments), [comments])
  const mine = byPath.get(`${projectId}|${path ?? ''}`) ?? NONE
  const threads = useMemo(() => buildThreads(mine), [mine])

  return (
    <Discussion
      projectId={projectId}
      path={path}
      threads={threads}
      placeholder={path ? 'Comment on this file…' : 'Comment on this project…'}
      empty={
        <EmptyState
          icon={MessageSquarePlus}
          title="No comments yet"
          hint={path ? 'Start a thread about this file.' : 'Start a thread about this project.'}
        />
      }
    />
  )
}

/**
 * A scrolling conversation with the composer docked beneath it.
 *
 * `threads` arrives sorted by LAST ACTIVITY (that is what `buildThreads`
 * promises); here they are laid out in the order they were STARTED, oldest at
 * the top, in one place so the side panel and the full-page feed read the same
 * way round. Ordering by activity would be right for an inbox and is wrong
 * here: answering a question three screens up would make the whole
 * conversation jump under the person who answered it.
 */
export function Discussion({ projectId, path, threads, placeholder, empty, showPath, header }: {
  projectId: ProjectId
  path?: string
  threads: readonly Thread[]
  placeholder: string
  empty: ReactNode
  /** Name the file each thread is about — for the project-wide feed. */
  showPath?: boolean
  header?: ReactNode
}) {
  const [replyingTo, setReplyingTo] = useState<string>()
  const pendingRows = usePending(s => s.rows)

  const ordered = useMemo(
    () => [...threads].sort((a, b) => a.root.seq - b.root.seq),
    [threads],
  )
  // A reply is placed by the thread it answers, not by what this surface is
  // about: the project-wide feed shows threads about files, and a pending
  // reply to one of them belongs under it here as well as in the file's panel.
  const here = pendingRows.filter(r => r.projectId === projectId)
  const roots = here.filter(r => !r.parentId && (r.path ?? '') === (path ?? ''))

  // Changes exactly when there is something new to look at — another thread,
  // another reply inside one, or one of our own writes still in flight. The
  // target is part of it because switching files replaces the whole list, and
  // two files with the same counts would otherwise keep each other's scroll.
  const replies = ordered.reduce((n, t) => n + t.replies.length, 0)
  const signal = `${projectId}|${path ?? ''}:${ordered.length}:${replies}:${here.length}`
  const { ref, atBottom, onScroll, jump } = useStickToBottom(signal)

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      <div className="relative min-h-0 flex-1">
        <div ref={ref} onScroll={onScroll} className="h-full overflow-y-auto overscroll-contain">
          {ordered.length === 0 && roots.length === 0 ? empty : (
            // Bottom-aligned until it overflows: two comments belong beside the
            // box you answer them in, not stranded at the top of an empty panel.
            <div className="flex min-h-full flex-col justify-end divide-y divide-[var(--border)]">
              {ordered.map(thread => (
                <ThreadView
                  key={thread.root.id}
                  thread={thread}
                  projectId={projectId}
                  path={path}
                  showPath={showPath}
                  pending={here.filter(r => r.parentId === thread.root.id)}
                  replying={replyingTo === thread.root.id}
                  onReply={() => setReplyingTo(thread.root.id)}
                  onCancelReply={() => setReplyingTo(undefined)}
                />
              ))}
              {roots.map(row => (
                <div key={row.id} className="px-3 py-2.5">
                  <PendingView row={row} />
                </div>
              ))}
            </div>
          )}
        </div>

        {!atBottom && (
          <button
            onClick={jump}
            className="pop-in absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--bg-raised)] px-2.5 py-1 text-xs font-medium shadow-pop hover:bg-[var(--bg-hover)]"
          >
            <ArrowDown className="h-3 w-3" strokeWidth={2} /> Jump to latest
          </button>
        )}
      </div>

      <div className="shrink-0 border-t border-[var(--border)] p-2">
        {/* Writing something is a decision to look at the bottom of the list,
            whatever you were reading a moment ago. */}
        <Composer projectId={projectId} path={path} placeholder={placeholder} onDone={jump} />
      </div>
    </div>
  )
}

function ThreadView({ thread, projectId, path, showPath, pending, replying, onReply, onCancelReply }: {
  thread: Thread
  projectId: ProjectId
  path?: string
  showPath?: boolean
  pending: readonly Pending[]
  replying: boolean
  onReply: () => void
  onCancelReply: () => void
}) {
  return (
    <div className="px-3 py-2.5">
      {showPath && thread.root.path && (
        <div className="mb-1.5 text-xs">
          <FileChip path={thread.root.path as Path} projectId={projectId} seq={thread.root.seq} />
        </div>
      )}

      <CommentView comment={thread.root} />

      {(thread.replies.length > 0 || pending.length > 0) && (
        <div className="mt-2 space-y-2 border-l border-[var(--border)] pl-3">
          {thread.replies.map(reply => <CommentView key={reply.id} comment={reply} />)}
          {pending.map(row => <PendingView key={row.id} row={row} />)}
        </div>
      )}

      {replying ? (
        <div className="mt-2 pl-3">
          <Composer
            projectId={projectId}
            // A reply is about whatever the ROOT is about. Taking the
            // surface's own target instead would file a reply written in the
            // project-wide feed under no file at all, and the file's own
            // comment panel — which selects by project and path — would then
            // never show it.
            path={thread.root.path ?? path}
            parentId={thread.root.id}
            placeholder={`Reply to ${personOf(ROSTER, thread.root.authorId).name.split(' ')[0]}…`}
            autoFocus
            onDone={onCancelReply}
            onCancel={onCancelReply}
          />
        </div>
      ) : (
        <button
          onClick={onReply}
          className="snap mt-1.5 inline-flex items-center gap-1 pl-3 text-xs text-[var(--fg-subtle)] hover:text-[var(--fg)]"
        >
          <Reply className="h-3 w-3" strokeWidth={1.75} /> Reply
        </button>
      )}
    </div>
  )
}

export function CommentView({ comment, showPath }: { comment: Comment; showPath?: boolean }) {
  const roster = useWorkspace(s => s.roster)
  const me = useWorkspace(s => s.user)
  const person = personOf(roster, comment.authorId)
  const [editing, setEditing] = useState(false)
  /** Why a delete did not happen. A mutation that fails in silence is a lie. */
  const [failure, setFailure] = useState<string>()
  const isMine = me?.id === comment.authorId

  if (comment.deletedAt) {
    return <p className="py-1 text-xs italic text-[var(--fg-subtle)]">{person.name} deleted a comment.</p>
  }

  return (
    <div className="group flex gap-2">
      <Avatar user={person} size={20} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm">
          <span className="font-medium">{person.name}</span>
          <Tooltip label={formatExact(comment.at)}>
            <span className="text-xs text-[var(--fg-subtle)]">{formatRelative(comment.at)}</span>
          </Tooltip>
          {comment.editedAt && <span className="text-xs text-[var(--fg-subtle)]">(edited)</span>}
          <div className="flex-1" />
          {isMine && !editing && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="snap rounded-[var(--radius-sm)] p-0.5 text-[var(--fg-subtle)] opacity-0 hover:bg-[var(--bg-hover)] focus-visible:opacity-100 group-hover:opacity-100" aria-label="Comment actions">
                  <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={4} className="pop-in z-50 min-w-32 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] p-1 shadow-pop">
                  <DropdownMenu.Item onSelect={() => setEditing(true)} className="snap cursor-pointer rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-[var(--bg-hover)]">
                    Edit
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => {
                      setFailure(undefined)
                      void api.deleteComment(comment.id)
                        .then(r => absorb(r.comment))
                        .catch(e => setFailure((e as Error).message))
                    }}
                    className="snap cursor-pointer rounded-[var(--radius-sm)] px-2 py-1.5 text-sm text-[var(--deleted)] outline-none data-[highlighted]:bg-[var(--bg-hover)]"
                  >
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>

        {showPath && comment.path && (
          <div className="truncate text-[11px] text-[var(--fg-subtle)]" title={comment.path}>
            {ellipsisPath(comment.path, 3)}
          </div>
        )}

        {editing ? (
          <Composer
            projectId={comment.projectId}
            editing={comment}
            autoFocus
            onDone={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <CommentBody body={comment.body} projectId={comment.projectId} seq={comment.seq} />
        )}

        {failure && <p className="mt-1 text-xs text-[var(--deleted)]">could not delete: {failure}</p>}
      </div>
    </div>
  )
}

/** A comment that has been written but not yet acknowledged by the server. */
function PendingView({ row }: { row: Pending }) {
  const roster = useWorkspace(s => s.roster)
  const person = personOf(roster, row.authorId)
  const failed = Boolean(row.error)

  return (
    <div className={cn('flex gap-2', !failed && 'opacity-60')}>
      <Avatar user={person} size={20} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm">
          <span className="font-medium">{person.name}</span>
          {failed ? (
            <span className="inline-flex items-center gap-1 text-xs text-[var(--deleted)]">
              <AlertTriangle className="h-3 w-3" strokeWidth={1.75} /> not sent
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-[var(--fg-subtle)]">
              <Spinner size={10} /> sending…
            </span>
          )}
        </div>

        <CommentBody body={row.body} projectId={row.projectId} seq={Number.MAX_SAFE_INTEGER} />

        {failed && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-[var(--fg-subtle)]">{row.error}</span>
            <Button size="sm" variant="secondary" onClick={() => void deliver(row)}>Retry</Button>
            <Button size="sm" variant="ghost" onClick={() => usePending.getState().drop(row.id)}>Discard</Button>
          </div>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* The composer                                                        */
/* ------------------------------------------------------------------ */

/**
 * DATA DEFINITION
 *   A Suggestion is { key, token, label, spans, detail, icon }.
 *   Interpretation: one row of the completion list — what it looks like
 *   (`label`, with `spans` marking the characters the query matched) and what
 *   accepting it writes into the body (`token`). People and files differ in
 *   every respect except these, which is why one list can draw both.
 */
interface Suggestion {
  readonly key: string
  readonly token: string
  readonly label: string
  readonly spans: readonly Span[]
  readonly detail: string
  readonly icon: ReactNode
}

/** How many completions to offer. More than this and nobody reads them. */
const MAX_SUGGESTIONS = 6

/** The height the textarea may grow to before it starts scrolling itself. */
const MAX_TEXTAREA_PX = 168

/**
 * Write or edit a comment.
 *
 * ⌘↵ submits, Escape cancels, "@" offers the roster and "/" offers the
 * project's files — the behaviours that make a comment box feel like part of a
 * keyboard-first product rather than a form.
 */
export function Composer({ projectId, path, parentId, editing, placeholder, autoFocus, onDone, onCancel }: {
  projectId: ProjectId
  path?: string
  parentId?: string
  editing?: Comment
  placeholder?: string
  autoFocus?: boolean
  onDone?: () => void
  onCancel?: () => void
}) {
  const me = useWorkspace(s => s.user)
  const changes = useWorkspace(s => s.changes)
  const [text, setText] = useState(editing?.body ?? '')
  const [caret, setCaret] = useState(editing?.body.length ?? 0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  /** The `from` of a trigger the person dismissed with Escape. */
  const [dismissed, setDismissed] = useState<number>()
  const [active, setActive] = useState(0)
  /** The file index is expensive enough to build only once somebody wants it. */
  const [wantsFiles, setWantsFiles] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { if (autoFocus) ref.current?.focus() }, [autoFocus])

  // Grow with the content rather than scrolling a three-line box. The dock
  // around it is `shrink-0`, so growing takes room from the list, not from the
  // buttons underneath.
  const grow = useCallback((): void => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_PX)}px`
  }, [])

  useEffect(grow, [text, grow])

  // How tall the text is depends on how wide the box is, so a height measured
  // at one width is simply wrong at another. Without this, a composer that
  // mounts in a narrow column stays that tall after the column widens.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let lastWidth = el.clientWidth
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return
      lastWidth = el.clientWidth
      grow()
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [grow])

  const trigger = useMemo(() => triggerAt(text, caret), [text, caret])
  const open = trigger && trigger.from !== dismissed ? trigger : undefined

  const files = useMemo(() => filesByProject(changes).get(projectId), [changes, projectId])
  const fileIndex = useMemo(
    () => (wantsFiles && files ? buildFileIndex(files) : undefined),
    [wantsFiles, files],
  )

  const suggestions = useMemo((): Suggestion[] => {
    if (!open) return []
    return open.kind === 'mention'
      ? peopleMatching(open.query)
      : filesMatching(fileIndex, open.query)
  }, [open, fileIndex])

  // A new query is a new list; keeping the old highlight would choose the wrong
  // row for anybody typing faster than they read. The list can also shrink
  // between a render and this effect, so what the keyboard acts on is clamped
  // rather than trusted.
  useEffect(() => { setActive(0) }, [open?.kind, open?.query])
  const activeIndex = Math.min(active, Math.max(0, suggestions.length - 1))

  // Built when the person first wants files, whether they typed "/" or simply
  // moved the caret back into a "/" they typed a moment ago — an index that
  // only appears for one of those two is an empty picker for the other.
  useEffect(() => { if (open?.kind === 'file') setWantsFiles(true) }, [open?.kind])

  /** Take an edit from the textarea, and decide what it is now offering. */
  const edit = (value: string, at: number): void => {
    setText(value)
    setCaret(at)
    const next = triggerAt(value, at)
    if (!next || next.from !== dismissed) setDismissed(undefined)
  }

  /** Accept a completion, putting the caret after what it inserted. */
  const choose = (suggestion: Suggestion): void => {
    if (!open) return
    const next = applyTrigger(text, open, caret, suggestion.token)
    setText(next.text)
    setCaret(next.caret)
    setDismissed(undefined)
    requestAnimationFrame(() => {
      ref.current?.focus()
      ref.current?.setSelectionRange(next.caret, next.caret)
    })
  }

  /** Send what has been written: an edit round-trips, a new comment does not. */
  const submit = useCallback(async () => {
    const valid = validateBody(text)
    if (!valid.ok) { setError(valid.error); return }
    setError(undefined)

    if (editing) {
      setBusy(true)
      try {
        const { comment } = await api.editComment(editing.id, valid.value)
        absorb(comment)
        onDone?.()
      } catch (e) {
        setError((e as Error).message)
      } finally {
        setBusy(false)
      }
      return
    }

    // Unsent comments belong on screen instantly; the round trip happens behind
    // the row that is already there.
    const row = usePending.getState().begin({
      projectId, path, parentId, body: valid.value, authorId: me?.id ?? ('unknown' as UserId),
    })
    setText('')
    setCaret(0)
    onDone?.()
    void deliver(row)
  }, [text, editing, projectId, path, parentId, me, onDone])

  /** The picker owns the arrow keys and Enter while it is open; nothing else. */
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive((activeIndex + 1) % suggestions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive((activeIndex - 1 + suggestions.length) % suggestions.length)
        return
      }
      if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey) || e.key === 'Tab') {
        e.preventDefault()
        choose(suggestions[activeIndex])
        return
      }
      if (e.key === 'Escape') {
        // Dismiss the picker only; the composer itself stays open, which is
        // what Escape means when something is layered over it.
        e.preventDefault()
        e.stopPropagation()
        setDismissed(open.from)
        return
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void submit() }
    if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel() }
  }

  const mentioned = parseMentions(text)
  const referenced = fileRefsIn(text)

  // The picker is a listbox the textarea drives without ever giving up focus,
  // which is the ARIA combobox pattern: without these the rows exist only for
  // people who can see them.
  const listId = useId()
  const optionId = (i: number): string => `${listId}-${i}`
  const showing = Boolean(open) && suggestions.length > 0

  return (
    <div className="relative">
      {showing && (
        <SuggestionList
          id={listId}
          optionId={optionId}
          label={open?.kind === 'mention' ? 'People' : 'Files in this project'}
          suggestions={suggestions}
          active={activeIndex}
          onHover={setActive}
          onChoose={choose}
        />
      )}

      <div className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-inset)] focus-within:border-[var(--border-strong)]">
        <textarea
          ref={ref}
          value={text}
          onChange={e => edit(e.target.value, e.target.selectionStart)}
          onSelect={e => setCaret((e.target as HTMLTextAreaElement).selectionStart)}
          onKeyDown={onKeyDown}
          rows={1}
          maxLength={MAX_COMMENT_LENGTH}
          placeholder={placeholder ?? 'Add a comment…'}
          aria-label={editing ? 'Edit comment' : 'Write a comment'}
          role="combobox"
          aria-expanded={showing}
          aria-autocomplete="list"
          aria-controls={showing ? listId : undefined}
          aria-activedescendant={showing ? optionId(activeIndex) : undefined}
          className="block w-full resize-none bg-transparent px-2 py-1.5 text-sm leading-relaxed outline-none placeholder:text-[var(--fg-subtle)]"
        />

        <div className="flex items-center gap-2 px-2 pb-1.5 text-[11px] text-[var(--fg-subtle)]">
          {/* A button, not a span: a tooltip on something that cannot take
              focus is help only a mouse can reach. */}
          <Tooltip label={<FormattingHelp />} side="top">
            <button type="button" className="snap inline-flex items-center gap-1 hover:text-[var(--fg)]">
              <Type className="h-3 w-3" strokeWidth={1.75} /> Markdown
            </button>
          </Tooltip>
          {mentioned.length > 0 && (
            <span className="truncate">
              notifies {mentioned.map(id => personOf(ROSTER, id).name.split(' ')[0]).join(', ')}
            </span>
          )}
          {referenced.length > 0 && (
            <span className="truncate">
              {referenced.length === 1 ? '1 file' : `${referenced.length} files`} referenced
            </span>
          )}
          <div className="flex-1" />
          {onCancel && <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>}
          <Button
            size="sm" variant="primary"
            onClick={() => void submit()}
            disabled={busy || text.trim().length === 0}
          >
            <Send className="h-3 w-3" strokeWidth={1.75} />
            {editing ? 'Save' : 'Comment'}
            {/* Printed, not hidden in a tooltip: a shortcut nobody can see is a
                shortcut nobody learns. */}
            <Kbd keys={[MOD, '↵']} />
          </Button>
        </div>
      </div>

      {error && <p className="mt-1 text-xs text-[var(--deleted)]">{error}</p>}
    </div>
  )
}

/** What the composer understands, for the hover on "Markdown". */
function FormattingHelp() {
  const rows: [string, string][] = [
    ['**bold**', 'bold'],
    ['*italic*', 'italic'],
    ['`code`', 'code'],
    ['- item', 'a list'],
    ['> quote', 'a quote'],
    ['@name', 'notify someone'],
    ['/', 'refer to a file'],
  ]
  return (
    <span className="grid grid-cols-[auto_auto] gap-x-2 gap-y-0.5 text-left">
      {rows.map(([syntax, meaning]) => (
        <span key={syntax} className="contents">
          <span className="font-mono text-[var(--fg-muted)]">{syntax}</span>
          <span>{meaning}</span>
        </span>
      ))}
    </span>
  )
}

function SuggestionList({ id, optionId, label, suggestions, active, onHover, onChoose }: {
  id: string
  optionId: (i: number) => string
  label: string
  suggestions: readonly Suggestion[]
  active: number
  onHover: (i: number) => void
  onChoose: (s: Suggestion) => void
}) {
  const ref = useRef<HTMLDivElement>(null)

  // A reply composer lives INSIDE the scrolling list, which clips what hangs
  // above it. Nudging the list is enough to bring the picker back into view,
  // and costs none of the focus trouble a portal would.
  useEffect(() => { ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' }) }, [])

  return (
    <div
      ref={ref}
      className="pop-in absolute bottom-full left-0 z-20 mb-1 w-80 max-w-full overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] p-1 shadow-pop"
    >
      {/* Only the options may be inside the listbox — the keyboard legend
          below it is decoration, and a listbox with a stray child is a
          listbox a screen reader reads wrong. */}
      <div id={id} role="listbox" aria-label={label}>
        {suggestions.map((suggestion, i) => (
          <button
            key={suggestion.key}
            id={optionId(i)}
            role="option"
            aria-selected={i === active}
            // Focus stays in the textarea — the caret is what the person is
            // aiming with — so these must not be Tab stops either.
            tabIndex={-1}
            // The textarea must keep the caret, so the press may not move focus.
            onMouseDown={e => { e.preventDefault(); onChoose(suggestion) }}
            onMouseEnter={() => onHover(i)}
            className={cn(
              'snap flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1 text-left text-sm',
              i === active && 'bg-[var(--bg-hover)]',
            )}
          >
            {suggestion.icon}
            {/* Two lines, not two columns: this panel is 320px wide and a CAD
                filename truncated to fit beside its folder tells you nothing. */}
            <span className="min-w-0 flex-1">
              <span className="block truncate">
                <Highlight text={suggestion.label} spans={suggestion.spans} />
              </span>
              <span className="block truncate text-[11px] text-[var(--fg-subtle)]">
                {suggestion.detail}
              </span>
            </span>
          </button>
        ))}
      </div>
      <div aria-hidden className="flex items-center gap-2 px-2 pt-1 text-[10px] text-[var(--fg-subtle)]">
        <Kbd keys={['↑', '↓']} /> move
        <Kbd keys={['↵']} /> insert
        <Kbd keys={['Esc']} /> dismiss
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Completions                                                         */
/* ------------------------------------------------------------------ */

/** People whose name or handle contains the query. */
function peopleMatching(query: string): Suggestion[] {
  const q = query.toLowerCase()
  return ROSTER
    .filter(u => u.name.toLowerCase().includes(q) || u.id.includes(q))
    .slice(0, MAX_SUGGESTIONS)
    .map(person => ({
      key: person.id,
      token: `@${person.id}`,
      label: person.name,
      spans: [],
      detail: person.id,
      icon: <Avatar user={person} size={18} />,
    }))
}

/**
 * A fuzzy index over one project's files.
 *
 * Boost rises with how recently a file was touched, so that typing "/" with no
 * query at all offers what the team is working on this week rather than
 * whatever sorts first.
 * buildFileIndex : FileSet -> SearchIndex<Path>
 */
function buildFileIndex(files: ReadonlyMap<string, { path: Path; updatedAt: string }>): SearchIndex<Path> {
  const entries = [...files.values()]
  const byAge = [...entries].sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))
  const rank = new Map(byAge.map((entry, i) => [entry.path, i]))
  const index = new SearchIndex<Path>()
  index.addAll(entries.map(entry => ({
    id: entry.path,
    kind: 'file' as const,
    title: basename(entry.path),
    subtitle: entry.path,
    haystack: entry.path,
    boost: 1 + ((rank.get(entry.path) ?? 0) / Math.max(1, entries.length)) * 0.4,
    payload: entry.path,
  })))
  return index
}

/**
 * Files matching the query, as rows ready to draw.
 *
 * The detail column names the FOLDER, not the path: the file's own name is
 * already the label, and in this corpus the folder is the disambiguator —
 * several assemblies each own a "Drawing/…​.PDF".
 */
function filesMatching(index: SearchIndex<Path> | undefined, query: string): Suggestion[] {
  if (!index) return []
  // Spaces are dropped from the query, never from the corpus: people type
  // "/cathode cup" for a file called "Cathode_Cup_Support.SLDPRT", and a space
  // the name spells with an underscore would otherwise fail to match at all.
  return index.search(query.replace(/\s+/g, ''), { limit: MAX_SUGGESTIONS }).map(match => {
    const folder = dirname(match.doc.payload)
    return {
      key: match.doc.id,
      token: formatFileRef(match.doc.payload),
      label: match.doc.title,
      spans: match.spans,
      detail: folder ? ellipsisPath(folder, 2) : 'project root',
      icon: <FileIcon path={match.doc.payload} />,
    }
  })
}
